import { useForm as createUseForm } from "@ant-design-vue/use";
import { notification } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { setIsLoading as setIsLoadingApp } from "../../../app.logic";
import { callAddressType, createForm as createFormAddress } from "../../../components/address/address.logic";
import { openModal as openModalOtp } from "../../../components/verify-otp/verify-otp.logic";
import i18n from "../../../i18n";
import router from "../../../router";
import store from "../../../store";
import { initUseForm, watchRulesChildren } from "../../../utils/form";
import { createLoading } from "../../../utils/loading";
import {
    email,
    hasIdCard,
    hasUsername,
    isAlphabetOrNumber,
    minLength,
    noHyphenValidator,
    required,
    requiredWhen,
    sameWhen,
    validateError
} from "../../../utils/validate";
import { createForm as createFormUser } from "../../app/setting/user-mgt/user/user.logic";
import { createConditionForm } from "./register.condition";
import { callPersonType, callRegisterAttachment, createField } from "./register.section";

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/security/user";

export const callUserRegister = ({ form }) => store.dispatch(`resource/v1/security/user/register`, { data: form });

export const onClickSave = ({ data, formParams, otp }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callUserRegister({ form: { user: { ...data.useForm.modelRef, ...formParams }, otp } })
                .finally(() => setIsLoadingApp(false))
                .then(() => {
                    router.push({ name: "login" });
                });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createForm = ({ props, data, refs }) => {
    return {
        ...createFormUser({ props, data, refs }),
        addresses: [createFormAddress(), createFormAddress()],
        acceptCondition: null,
        isAcceptPdpa: false,
    };
};

export const createRules = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}refPersonType`]: [required],
        [`${prefix}refDepartmentRegister`]: [required],
        [`${prefix}refCompanyType`]: [requiredWhen({ when: () => model.refPersonType?.key === "1" })],
        [`${prefix}companyNameTh`]: [requiredWhen({ when: () => model.refPersonType?.key === "1" })],
        [`${prefix}companyNameEn`]: [requiredWhen({ when: () => model.refPersonType?.key === "1" })],
        [`${prefix}refDocumentTypePerson`]: [requiredWhen({ when: () => model.refPersonType?.key === "2" })],
        [`${prefix}idCard`]: [required, noHyphenValidator(), isAlphabetOrNumber,
            {
                validator: (rule, value) => {
                    if (value && (!modelRef.refRequestType || modelRef.refRequestType?.key === "1")) {
                        if (value.length !== 13) {
                            if (model.refPersonType?.key === "1" || model.refDocumentTypePerson?.key === "1") {
                                return Promise.reject(i18n.global.t("error.form.equalsLength", { equals: 13 }));
                            }
                        }
                    }
                    return Promise.resolve();
                },
                trigger: "blur"
            },
            hasIdCard({ cardKey: () => model.refDocumentTypePerson?.key }),
        ],
        [`${prefix}refIssuedBy`]: [requiredWhen({ when: () => !model.id && model.refPersonType?.key === "2" })],
        [`${prefix}outDate`]: [requiredWhen({ when: () => !model.id && model.refPersonType?.key === "2" })],
        [`${prefix}expireDate`]: [requiredWhen({ when: () => !model.id && model.refPersonType?.key === "2" })],
        [`${prefix}birthDate`]: [requiredWhen({ when: () => model.refPersonType?.key === "2" })],
        [`${prefix}refNationality`]: [requiredWhen({ when: () => model.refPersonType?.key === "2" })],
        [`${prefix}refRace`]: [requiredWhen({ when: () => model.refPersonType?.key === "2" })],
        [`${prefix}refReligion`]: [requiredWhen({ when: () => model.refPersonType?.key === "2" })],
        [`${prefix}refGender`]: [requiredWhen({ when: () => model.refPersonType?.key === "2" })],
        [`${prefix}refTitle`]: [requiredWhen({ when: () => model.refPersonType?.key === "2" })],
        [`${prefix}firstNameTh`]: [requiredWhen({ when: () => model.refPersonType?.key === "2" })],
        [`${prefix}firstNameEn`]: [requiredWhen({ when: () => model.refPersonType?.key === "2" })],
        [`${prefix}lastNameTh`]: [requiredWhen({ when: () => model.refPersonType?.key === "2" })],
        [`${prefix}lastNameEn`]: [requiredWhen({ when: () => model.refPersonType?.key === "2" })],
        [`${prefix}mobile`]: [required],
        [`${prefix}addresses.0.refAddressType`]: [required],
        [`${prefix}addresses.0.noTh`]: [required],
        [`${prefix}addresses.0.noEn`]: [requiredWhen({ when: () => !!model.userObjectives?.find(d => !d.isDelete && d.refObjective?.key === "7") })],
        [`${prefix}addresses.0.refProvince`]: [required],
        [`${prefix}addresses.0.refAmphur`]: [required],
        [`${prefix}addresses.0.refTambon`]: [required],
        [`${prefix}addresses.0.postCode`]: [required],
        [`${prefix}addresses.0.tel`]: [required],
        [`${prefix}addresses.0.latitude`]: [required],
        [`${prefix}addresses.0.longitude`]: [required],
        [`${prefix}addresses.1.refAddressType`]: [required],
        [`${prefix}addresses.1.noTh`]: [required],
        [`${prefix}addresses.1.noEn`]: [requiredWhen({ when: () => !!model.userObjectives?.find(d => !d.isDelete && d.refObjective?.key === "7") })],
        [`${prefix}addresses.1.refProvince`]: [required],
        [`${prefix}addresses.1.refAmphur`]: [required],
        [`${prefix}addresses.1.refTambon`]: [required],
        [`${prefix}addresses.1.postCode`]: [required],
        [`${prefix}addresses.1.tel`]: [required],
        [`${prefix}addresses.1.latitude`]: [required],
        [`${prefix}addresses.1.longitude`]: [required],
        [`${prefix}userOperatorTypes`]: [required],
        [`${prefix}userObjectives`]: [required],
        [`${prefix}username`]: [required, email, hasUsername],
        [`${prefix}password`]: [required, minLength(4)],
        [`${prefix}passwordConfirm`]: [
            sameWhen({
                when: () => {
                    const { useForm } = data;
                    const { modelRef } = useForm;
                    const model = key ? lodash.get(modelRef, key) : modelRef;
                    return model.password === model.passwordConfirm;
                },
                keyLabel: i18n.global.t("model.user.password"),
                sameLabel: i18n.global.t("model.user.passwordConfirm")
            })
        ],
        [`${prefix}acceptCondition`]: [
            {
                validator: (rule, value) => {
                    if (value !== true) {
                        return Promise.reject(i18n.global.t("error.form.acceptCondition"));
                    }
                    return Promise.resolve();
                },
                trigger: "change"
            }
        ]
    };
    return rules;
};

export const createRulesAttachment = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}attachments.${index}.documentName`] = [required];
            rules[`${prefix}attachments.${index}.size`] = [requiredWhen({ when: () => model.attachments[index].attribute?.isRequiredAttachment })];
        }
    });
    return rules;
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { useForm } = data;
    const { modelRef } = useForm;
    let promise = Promise.resolve({ data: [modelRef] });
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ data }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const promises = [callPersonType({}), callAddressType({}), callRegisterAttachment({})];
    return Promise.all(promises).then(arr => {
        data.personTypes = arr[0];
        data.addressTypes = arr[1];
        data.registerAttachments = arr[2];

        modelRef.registerDate = moment().format("YYYY-MM-DD");
        modelRef.refPersonType = data.personTypes.find(d => d.key === "1");
        modelRef.addresses[0].refAddressType = data.addressTypes.find(d => d.key === "1");
        modelRef.addresses[1].refAddressType = data.addressTypes.find(d => d.key === "2");
        data.registerAttachments.forEach(item =>
            modelRef.attachments.push({ documentName: item.value, attribute: lodash.cloneDeep(item.attribute) })
        );
    });
};

export const initForm = ({ props, data, refs }) => {
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.attachments.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesAttachment({ props, data, refs }),
        itemsKey: "attachments"
    });
    loadForm({ props, data, refs });
};

export const onClickOpenModalOtp = ({ props, data, refs }) => {
    data.useForm
        .validate()
        .then(() => {
            openModalOtp({
                props: { dataProp: { form: { email: data.useForm.modelRef.email } } },
                onOkCallback: ({ otp, options }) => {
                    onClickSave({ props, data, refs, otp }).then(() => {
                        notification["success"]({ message: i18n.global.t("views.register.success") });
                        options.bind.onCancel();
                    });
                }
            });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};
